import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DocumentDto } from 'src/api/FiretrolApiClient';
import { useDocumentFolders } from 'src/api/queries/documents/useDocumentFolders';
import { useDocumentList } from 'src/api/queries/documents/useDocumentList';
import { useDocumentsInFolder } from 'src/api/queries/documents/useDocumentsInFolder';
import { queryKeys } from 'src/api/queries/queryKeys';
import { ROOT_FOLDER_ID } from 'src/app/documents/documentConstants';
import { findFolderPath } from 'src/app/documents/folderPath';

export interface IJobDocuments {
  folders: DocumentDto[];
  documents: DocumentDto[];
  currentFolderId: string;
  masterId: string;
  parentFolder: DocumentDto | null;
  subfolders: DocumentDto[];
  breadcrumbs: DocumentDto[];
  isSelectedLocked: boolean;
  documentsLoading: boolean;
  selectFolder: (folderId: string) => void;
  resetToRoot: () => void;
  refreshFolders: () => void;
  refreshDocuments: () => void;
}

export const useJobDocuments = (jobId?: number): IJobDocuments => {
  const qc = useQueryClient();
  const [currentFolderId, setCurrentFolderId] =
    React.useState<string>(ROOT_FOLDER_ID);

  const isRoot = currentFolderId === ROOT_FOLDER_ID;

  const foldersQuery = useDocumentFolders(jobId);
  const rootDocumentsQuery = useDocumentList(jobId, isRoot);
  const folderDocumentsQuery = useDocumentsInFolder(
    jobId,
    isRoot ? null : currentFolderId,
  );

  const folders = foldersQuery.data ?? [];
  const documentsQuery = isRoot ? rootDocumentsQuery : folderDocumentsQuery;

  const resetToRoot = React.useCallback(
    () => setCurrentFolderId(ROOT_FOLDER_ID),
    [],
  );

  // Re-derived from `folders` on every refresh so a rename, a new subfolder or a
  // deletion reaches the breadcrumb and the folder table without a second source
  // of truth to keep in sync.
  const breadcrumbs = React.useMemo(
    () => (isRoot ? [] : findFolderPath(folders, currentFolderId)),
    [folders, currentFolderId, isRoot],
  );

  const parentFolder = breadcrumbs[breadcrumbs.length - 1] ?? null;

  const selectFolder = React.useCallback(
    (folderId: string) => {
      if (!folderId || folderId === ROOT_FOLDER_ID) {
        setCurrentFolderId(ROOT_FOLDER_ID);
        return;
      }
      if (findFolderPath(folders, folderId).length === 0) return;
      setCurrentFolderId(folderId);
    },
    [folders],
  );

  const refreshFolders = React.useCallback(() => {
    qc.invalidateQueries({
      queryKey: queryKeys.documents.folders(jobId ?? -1),
    });
  }, [qc, jobId]);

  const refreshDocuments = React.useCallback(() => {
    qc.invalidateQueries({
      queryKey: isRoot
        ? queryKeys.documents.list(jobId ?? -1)
        : queryKeys.documents.inFolder(jobId ?? -1, currentFolderId),
    });
  }, [qc, jobId, isRoot, currentFolderId]);

  return {
    folders,
    documents: documentsQuery.data ?? [],
    currentFolderId,
    masterId: breadcrumbs[0]?.id ?? ROOT_FOLDER_ID,
    parentFolder,
    subfolders: parentFolder ? parentFolder.children ?? [] : folders,
    breadcrumbs,
    isSelectedLocked: breadcrumbs[0]?.isLocked ?? false,
    documentsLoading: documentsQuery.isLoading,
    selectFolder,
    resetToRoot,
    refreshFolders,
    refreshDocuments,
  };
};
